<template>
  <div>
    <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t("title") }}</h2>
    <Loading v-if="loading" />
    <p v-else-if="!items.length" class="text-sm text-gray-500" data-test="category-repartition-empty">
      {{ t("no_results") }}
    </p>
    <div v-else class="relative">
      <ul
        ref="list"
        class="category-list space-y-6 overflow-y-auto pr-2"
        data-test="category-repartition-list"
        @scroll="handleScroll"
      >
        <li v-for="(item, index) in items" :key="item.category" data-test="category-repartition-item">
          <p class="text-sm font-medium text-gray-700">{{ item.name }}</p>
          <div class="flex items-center justify-between mt-2">
            <p class="flex items-baseline gap-2">
              <span class="text-3xl font-bold text-gray-900">{{ item.current }}</span>
              <span class="text-base font-medium text-primary-dark">{{ item.share }} %</span>
            </p>
            <Variation :variation="item.variation ?? null" />
          </div>
          <div class="mt-2 h-2 w-full rounded-full bg-gray-100">
            <div
              class="h-2 rounded-full"
              :class="index === 0 ? 'bg-primary-dark' : 'bg-primary-lighter'"
              :style="{ width: `${item.share}%` }"
            />
          </div>
          <p v-if="item.previous != null" class="mt-2 text-xs text-gray-500">
            {{ item.previous }} {{ t(comparisonType) }}
          </p>
        </li>
      </ul>
      <div
        v-if="showScrollHint"
        class="absolute inset-x-0 bottom-0 flex justify-center items-end h-12 bg-gradient-to-t from-white pointer-events-none"
        data-test="category-repartition-scroll-hint"
      >
        <ChevronDownIcon class="h-4 w-4 text-gray-400" aria-hidden="true" />
        <span class="sr-only">{{ t("scroll_hint") }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { Loading, Variation } from "@estaly/ui";
import { ChevronDownIcon } from "@heroicons/vue/outline";

// The list height fits exactly this many plans, the others are reachable by scrolling.
const VISIBLE_ITEMS = 2;

export default {
  name: "CategoryRepartition",
  components: { ChevronDownIcon, Loading, Variation },

  props: {
    loading: { type: Boolean, default: false },
    repartition: { type: Array, default: () => [] },
    comparisonType: { type: String, default: "previousPeriod" },
  },

  data() {
    return { scrolledToEnd: false };
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  computed: {
    plans() {
      return this.repartition || [];
    },

    total() {
      return this.plans.reduce((sum, { current }) => sum + current, 0);
    },

    items() {
      return this.plans.map((item) => ({
        ...item,
        share: this.total ? Math.round((item.current * 100) / this.total) : 0,
      }));
    },

    showScrollHint() {
      return this.items.length > VISIBLE_ITEMS && !this.scrolledToEnd;
    },
  },

  watch: {
    repartition() {
      this.scrolledToEnd = false;
      if (this.$refs.list) this.$refs.list.scrollTop = 0;
    },
  },

  methods: {
    handleScroll({ target }) {
      this.scrolledToEnd = target.scrollTop + target.clientHeight >= target.scrollHeight - 1;
    },
  },
};
</script>

<style scoped>
/* Exactly two plans (VISIBLE_ITEMS) fit, aligned with the chart height. */
.category-list {
  max-height: 14.5rem;
}
</style>

<i18n src="./CategoryRepartition.i18n.json"></i18n>
